import { useTranslation } from 'react-i18next';

import { Button, Modal } from './ui';
import { cn } from '../lib/cn';
import { colorForClassIndex } from '../utils/classColors';
import type { BoundingBox, Class as ClassModel } from '../types/api';

/**
 * ClassPickerModal — surfaced from the sidebar filter dropdown's
 * "Select by class…" option. Shows every class as a clickable card with
 * its color dot + name + count of boxes drawn project-wide. Picking one
 * sets the image-list class filter; the dropdown chip then reads
 * "Class: <name>".
 *
 * Mutually exclusive with the status filter — picking a class clears
 * whatever status was active. Simpler mental model than two filter
 * dimensions in one widget; if "Pending AND has-apple" becomes a real
 * workflow, lift to two dropdowns later.
 */
interface ClassPickerModalProps {
    open: boolean;
    onClose: () => void;
    isDarkMode?: boolean;
    classes: ClassModel[];
    /** ALL boxes loaded for the active image — used only as a per-class
     *  hint; full project-wide counts would require an extra fetch. */
    activeImageBoxes: BoundingBox[];
    onPick: (classIndex: number) => void;
    onClear: () => void;
}

export default function ClassPickerModal({
    open,
    onClose,
    isDarkMode = false,
    classes,
    activeImageBoxes,
    onPick,
    onClear,
}: ClassPickerModalProps) {
    const { t } = useTranslation('canvas');
    const { t: tCommon } = useTranslation('common');

    const onActiveCount = (idx: number) =>
        activeImageBoxes.filter((b) => b.class_index === idx).length;

    return (
        <Modal
            open={open}
            onClose={onClose}
            title={t('class_picker_title', { defaultValue: 'Filter by class' })}
            widthClass="max-w-md"
            isDarkMode={isDarkMode}
            footer={
                <>
                    <Button variant="ghost" isDarkMode={isDarkMode} onClick={onClose}>
                        {tCommon('cancel')}
                    </Button>
                    <Button
                        variant="ghost"
                        isDarkMode={isDarkMode}
                        onClick={() => { onClear(); onClose(); }}
                    >
                        {t('class_picker_clear', { defaultValue: 'Show all classes' })}
                    </Button>
                </>
            }
        >
            <p className={cn('text-sm mb-3', isDarkMode ? 'text-gray-400' : 'text-gray-600')}>
                {t('class_picker_intro', {
                    defaultValue: 'Pick a class — the image list will narrow to images containing at least one box of that class.',
                })}
            </p>
            {classes.length === 0 ? (
                <p className={cn('text-sm italic py-6 text-center', isDarkMode ? 'text-gray-500' : 'text-gray-400')}>
                    {t('no_classes_yet', { defaultValue: 'No classes yet — add one to start filtering.' })}
                </p>
            ) : (
                <ul className="space-y-1.5">
                    {classes.map((c) => {
                        const color = c.color || colorForClassIndex(c.class_index);
                        const localCount = onActiveCount(c.class_index);
                        return (
                            <li key={c.id}>
                                <button
                                    type="button"
                                    onClick={() => { onPick(c.class_index); onClose(); }}
                                    className={cn(
                                        'w-full flex items-center gap-3 px-3 py-2.5 rounded-lg border text-left transition-colors',
                                        isDarkMode
                                            ? 'border-gray-700 hover:border-primary-500 hover:bg-gray-800/40'
                                            : 'border-gray-200 hover:border-primary-400 hover:bg-primary-50/40',
                                    )}
                                >
                                    <span
                                        className="h-4 w-4 rounded-full shrink-0"
                                        style={{ backgroundColor: color }}
                                        aria-hidden="true"
                                    />
                                    <span className={cn('flex-1 text-sm font-medium', isDarkMode ? 'text-gray-100' : 'text-gray-900')}>
                                        {c.name}
                                    </span>
                                    {localCount > 0 && (
                                        <span className={cn(
                                            'text-[11px] tabular-nums px-1.5 py-0.5 rounded-full',
                                            isDarkMode ? 'bg-gray-800 text-gray-400' : 'bg-gray-100 text-gray-500',
                                        )}
                                        title={t('class_picker_active_image_count', {
                                            defaultValue: 'Boxes of this class on the current image',
                                        })}
                                        >
                                            {localCount}
                                        </span>
                                    )}
                                </button>
                            </li>
                        );
                    })}
                </ul>
            )}
        </Modal>
    );
}
